import * as t from 'io-ts';
import {isUndefined} from 'lodash';

import {makeOptional} from '../../libs/shared/core/src/models/api/types/optionalType';
import {ActivityModel} from '../models/api/activityModel';

// TODO: Reduce duplication with the P2/cells equivalent of this module.

/*
 * Runtime types.
 */

export const RConversationTimelineBoundary = t.type({
  activityDate: t.number,
  activityId: t.number,
});

export const RConversationTimelineRange = t.type({
  topBoundary: makeOptional(RConversationTimelineBoundary),
  bottomBoundary: makeOptional(RConversationTimelineBoundary),
});

export const RConversationTimelineMetadata = t.intersection([
  RConversationTimelineRange,
  t.type({
    activityIds: t.readonlyArray(t.number),
  }),
]);

/*
 * Types.
 */

export type ConversationTimelineBoundary = t.TypeOf<typeof RConversationTimelineBoundary>;
export type ConversationTimelineRange = t.TypeOf<typeof RConversationTimelineRange>;
export type ConversationTimelineMetadata = t.TypeOf<typeof RConversationTimelineMetadata>;

/*
 * Helpers.
 */

export function parseConversationTimelineBoundary(src: any) {
  return {
    activityDate: src.date,
    activityId: src.id,
  };
}

export function buildConversationTimelineBoundary(activity: ActivityModel): ConversationTimelineBoundary {
  return {
    activityDate: activity.date,
    activityId: activity.id,
  };
}

export function isWithinConversationTimelineRange(
  boundaries: ConversationTimelineRange,
  item: ConversationTimelineBoundary,
) {
  const {topBoundary, bottomBoundary} = boundaries;
  const isWithinTop = compareConversationTimelineBoundaries(topBoundary, item) <= 0;
  const isWithinBottom = compareConversationTimelineBoundaries(bottomBoundary, item) >= 0;

  return isWithinTop && isWithinBottom;
}

export function compareConversationTimelineBoundaries(
  a: ConversationTimelineBoundary | undefined,
  b: ConversationTimelineBoundary | undefined,
): number {
  // If either boundary is part of an open interval, consider them to be equal.
  if (!a || !b) {
    return 0;
  }

  // Compare by date.
  const dateDifference = a.activityDate - b.activityDate;
  if (dateDifference !== 0) {
    return dateDifference;
  }

  // Compare by ID.
  return a.activityId - b.activityId;
}

export function isActivityDateWithinConversationTimelineRange(
  boundaries: ConversationTimelineRange,
  activityDate: number,
): boolean {
  const topActivityDate = boundaries.topBoundary?.activityDate;
  const bottomActivityDate = boundaries.bottomBoundary?.activityDate;

  // Check we're either loaded to the top/bottom, or the date is strictly before the top/bottom (because there may be
  // other activities with the same date).
  const isWithinTop = isUndefined(topActivityDate) || topActivityDate < activityDate;
  const isWithinBottom = isUndefined(bottomActivityDate) || activityDate < bottomActivityDate;

  return isWithinTop && isWithinBottom;
}
